import { allArticles } from './articles-data.generated';

export interface ArticleSummary {
  id: string;
  title: string;
  desc: string;
  image: string;
  createdAt: string;
  featured?: boolean;
  language: 'en' | 'pt';
  groupId: string;
}

export interface ArticleDetail extends ArticleSummary {
  category: string;
  author?: string;
  tags: string[];
  source: string;
  updatedAt: string;
  content: string;
  sourceUrl: string;
}

const normalizeArticleDate = (value: string) => {
  if (!value) return value;
  return value.replace(/ZT\d{2}:\d{2}:\d{2}Z$/, 'Z');
};

const normalizeArticle = (article: ArticleDetail): ArticleDetail => ({
  ...article,
  createdAt: normalizeArticleDate(article.createdAt),
  updatedAt: normalizeArticleDate(article.updatedAt),
});

const essayArticles = allArticles
  .map(normalizeArticle)
  .filter((article) => article.category === 'essay');

export const articlesIndex: ArticleSummary[] = essayArticles.map(
  ({ id, title, desc, image, createdAt, featured, language }) => ({
    id,
    title,
    desc,
    image,
    createdAt,
    featured,
    language,
  }),
);

export const articleMap: Record<string, ArticleDetail> = Object.fromEntries(
  essayArticles.map((article) => [article.id, article] as const),
);

const articleGroups: Record<string, ArticleDetail[]> = essayArticles.reduce(
  (groups, article) => {
    const group = groups[article.groupId] ?? [];
    group.push(article);
    groups[article.groupId] = group;
    return groups;
  }, {} as Record<string, ArticleDetail[]>,
);

export function getArticleById(id: string): ArticleDetail | null {
  return articleMap[id] ?? null;
}

export function getArticleByGroupAndLanguage(groupId: string, language: 'en' | 'pt'): ArticleDetail | null {
  const group = articleGroups[groupId];
  if (!group) return null;
  return group.find((article) => article.language === language) ?? null;
}
